import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { keys, branchKey } from '@/lib/queryKeys';
import { listPrescriptionsForPatient, closePrescription, getPrescriptionVersioned } from '@/features/consultation/clinicalApi';

/** Every prescription this patient has ever had, across every visit/branch — tenant-wide, see clinicalApi.ts. */
export function usePatientPrescriptions(uhid: string | null) {
  return useQuery({
    queryKey: keys.clinical.prescriptionsForPatient(uhid ?? ''),
    queryFn: () => listPrescriptionsForPatient(uhid as string),
    enabled: !!uhid,
  });
}

/** One prescription's medicine lines, fetched only when a row is expanded — see PatientPrescriptionHistory.tsx. */
export function usePrescriptionDetail(id: number | null) {
  return useQuery({
    queryKey: keys.prescriptions.detail(id ?? -1),
    queryFn: () => getPrescriptionVersioned(id as number).then((v) => v.data),
    enabled: id != null,
  });
}

/**
 * Mark completed / cancel — PATCH /prescriptions/:id/close (never
 * PATCH /:id/status, which is prescriber-only; see that route's own
 * comment). Invalidates the patient's cross-visit list (this uhid may not
 * be known to every caller, hence the optional param) plus the two lists
 * every OTHER closure already invalidates: Dispensing's own "Active
 * prescriptions" and this prescription's own detail.
 */
export function useClosePrescription(uhid?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, reason }: { id: number; status: 'completed' | 'cancelled'; reason?: string }) =>
      closePrescription(id, status, reason),
    onSuccess: (_data, variables) => {
      if (uhid) queryClient.invalidateQueries({ queryKey: keys.clinical.prescriptionsForPatient(uhid) });
      queryClient.invalidateQueries({ queryKey: keys.prescriptions.list({ status: 'active' }) });
      queryClient.invalidateQueries({ queryKey: keys.prescriptions.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: branchKey('prescription') });
    },
  });
}
